/**
 * Platform bridge for geofence registration and native OS callback wiring.
 */

import type { SiteGeoFence } from '../services/geofence';

// Extend window interface for typescript compilation
declare global {
  interface Window {
    webkit?: {
      messageHandlers?: {
        registerGeofences?: {
          postMessage: (message: string) => void;
        };
      };
    };
    AndroidGeofenceBridge?: {
      registerGeofences: (geofencesJson: string) => void;
    };
    onGeofenceEnter?: (geofenceId: string, latitude?: number, longitude?: number) => void;
    simulateGeofenceEnter?: (geofenceId: string, latitude?: number, longitude?: number) => void;
  }
}

// Optimization: Keep track of currently registered geofence IDs to perform diffing
let lastRegisteredGeofenceIds = new Set<string>();

/**
 * Register geofences with iOS/Android native shell region monitoring APIs.
 */
export function registerGeofencesWithNative(geofences: SiteGeoFence[]) {
  const currentIds = geofences.map((g) => g.public_id);
  const currentSet = new Set(currentIds);

  // Set-diff check: Skip registration if the exact same set of geofences is already registered
  if (
    lastRegisteredGeofenceIds.size === currentSet.size &&
    [...currentSet].every((id) => lastRegisteredGeofenceIds.has(id))
  ) {
    if (import.meta.env.DEV) {
      console.log('[GeofenceBridge] Geofence set unchanged. Skipping registration.');
    }
    return;
  }

  lastRegisteredGeofenceIds = currentSet;

  const payload = JSON.stringify(geofences);
  if (import.meta.env.DEV) {
    console.log('[GeofenceBridge] Registering geofences with native:', geofences.length);
  }

  // iOS CLMonitor/CLCircularRegion native wiring
  if (window.webkit?.messageHandlers?.registerGeofences) {
    try {
      window.webkit.messageHandlers.registerGeofences.postMessage(payload);
      if (import.meta.env.DEV) {
        console.log('[GeofenceBridge] Successfully posted geofences to iOS webkit handler.');
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('[GeofenceBridge] iOS postMessage error:', err);
      }
    }
  }

  // Android GeofencingClient native wiring
  if (window.AndroidGeofenceBridge?.registerGeofences) {
    try {
      window.AndroidGeofenceBridge.registerGeofences(payload);
      if (import.meta.env.DEV) {
        console.log('[GeofenceBridge] Successfully registered geofences with Android bridge.');
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('[GeofenceBridge] Android bridge error:', err);
      }
    }
  }
}

/**
 * Initialize global callbacks for native geofence entry events.
 */
export function initializeGeofenceNativeBridge() {
  if (typeof window === 'undefined') return;

  // Set up the window-level listener callback called by mobile OS wrappers
  window.onGeofenceEnter = (geofenceId: string, latitude?: number, longitude?: number) => {
    if (import.meta.env.DEV) {
      console.log('[GeofenceBridge] Geofence enter event received:', geofenceId, latitude, longitude);
    }
    const event = new CustomEvent('geofence-enter', {
      detail: { geofenceId, latitude, longitude },
    });
    window.dispatchEvent(event);
  };

  // Provide a simulator trigger for browser debugging
  if (import.meta.env.DEV) {
    window.simulateGeofenceEnter = (geofenceId: string, latitude?: number, longitude?: number) => {
      console.log('[GeofenceBridge] Simulating geofence enter:', geofenceId);
      if (window.onGeofenceEnter) {
        window.onGeofenceEnter(geofenceId, latitude, longitude);
      }
    };
  }
}

export interface GeofenceEnterEventDetail {
  geofenceId: string;
  latitude?: number;
  longitude?: number;
}

/**
 * Clear the internal registered geofences cache (for testing purposes).
 */
export function _clearRegisteredGeofencesCache() {
  lastRegisteredGeofenceIds.clear();
}
